import { makeNoiseGLSL } from './noise.js'
import {
  colorGLSL,
  colorDefaults,
  createColorUniforms,
  setColorUniforms,
} from './color.js'
import { animDefaults } from './animation.js'

export const vertSrc = `
attribute vec2 a_position;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
}`

const FRAG_PRECISION = 'highp'

export function makePrepassFragSrc(octaves) {
  return `
precision ${FRAG_PRECISION} float;

uniform vec2 u_resolution;
uniform vec2 u_angleRot;
uniform float u_warpScale;
uniform vec2 u_noiseOffset;
uniform float u_noiseInvert;
uniform float u_animMoveEnabled;
uniform float u_animMoveSpeed;
uniform float u_animTime;

${makeNoiseGLSL(octaves)}

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution;

  float ca = u_angleRot.x;
  float sa = u_angleRot.y;
  vec2 st = uv - 0.5;
  st = mat2(ca, -sa, sa, ca) * st;
  st += 0.5;

  float noiseZ = u_animMoveEnabled > 0.5 ? u_animTime * u_animMoveSpeed : 0.0;
  float n1 = sampleNoise(st, u_warpScale, u_noiseOffset, u_noiseInvert, noiseZ);
  float n2 = sampleNoise(st, u_warpScale, u_noiseOffset + vec2(100.0), u_noiseInvert, noiseZ);

  gl_FragColor = vec4(n1, n2, 0.0, 1.0);
}`
}

export function makeFragSrc() {
  return `
#extension GL_OES_standard_derivatives : enable
precision ${FRAG_PRECISION} float;

uniform vec2 u_resolution;
uniform sampler2D u_noiseTex;
uniform vec2 u_noiseTexSize;

// ridge layout
uniform float u_frequency;
uniform float u_ridgeCount;
uniform float u_ridgeOffset;
uniform float u_ridgeOffsetB;
uniform float u_ridgeDual;
uniform vec2 u_angleRot;  // (cos(angle), sin(angle)) precomputed CPU-side

// gradient strip shape
uniform float u_shadow;
uniform float u_highlight;
uniform float u_peakPos;
uniform float u_riseCurve;
uniform float u_fallCurve;
uniform float u_invert;

// noise warp
uniform float u_warpAmount;
uniform float u_warpMargin;
uniform float u_noiseLit;

// render toggles
uniform float u_ridgesEnabled;

// animation
uniform float u_animPeakEnabled;
uniform float u_animPeakCentre;
uniform float u_animPeakAmplitude;
uniform float u_animPeakSpeed;
uniform float u_animPeakStagger;
uniform float u_animTime;

${colorGLSL}

// Smooth bilinear fetch. Hardware bilinear has piecewise-constant derivatives
// at texel boundaries, which propagate into the warp and show up as kinks on
// the ridge isolines. A cubic smoothstep of the fractional part makes the
// interpolation C1-continuous for a handful of ops, so a small noise FBO can
// be upscaled aggressively without visible jaggies.
vec4 smoothTex(sampler2D tex, vec2 uv, vec2 texSize) {
  vec2 t = uv * texSize + 0.5;
  vec2 i = floor(t);
  vec2 f = t - i;
  f = f * f * (3.0 - 2.0 * f);
  vec2 smoothed = (i + f - 0.5) / texSize;
  return texture2D(tex, smoothed);
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution;

  // Prepass already applied the UV rotation, so sampling at uv gives the value
  // at the rotated coordinate without the main pass having to rotate for lookup.
  vec2 n = smoothTex(u_noiseTex, uv, u_noiseTexSize).rg;
  float n1 = n.r;
  float n2 = n.g;

  // rotate UVs for ridge/warp math
  float ca = u_angleRot.x;
  float sa = u_angleRot.y;
  vec2 st = uv - 0.5;
  st = mat2(ca, -sa, sa, ca) * st;
  st += 0.5;

  // warp UVs — noise-driven organic contour distortion
  st += vec2(n1 - 0.5, n2 - 0.5) * 2.0 * u_warpAmount;

  float rawA = st.y * u_frequency + u_ridgeOffset;
  float rawB = st.y * u_frequency + u_ridgeOffsetB;

  // 1. surface — colour map is the base layer
  vec3 surface = sampleColorGradient(uv);

  bool inA = rawA >= -u_warpMargin && rawA <= u_ridgeCount + u_warpMargin;
  bool inB = u_ridgeDual > 0.5 && rawB >= -u_warpMargin && rawB <= u_ridgeCount + u_warpMargin;

  if (u_ridgesEnabled < 0.5 || (!inA && !inB)) {
    gl_FragColor = vec4(applyWebappOverlay(surface, uv), 1.0);
    return;
  }

  float raw = inA ? rawA : rawB;

  float ridgeIndex = floor(raw);
  float ramp = 1.0 - fract(raw);

  float animIndex = ridgeIndex;
  if (!inA && inB) animIndex += u_ridgeCount;

  float basePeak = u_peakPos;
  if (u_animPeakEnabled > 0.5) {
    float phase = u_animTime * u_animPeakSpeed * 6.2831853
                + animIndex * u_animPeakStagger;
    basePeak = u_animPeakCentre + u_animPeakAmplitude * sin(phase);
  }
  float peak = clamp(basePeak, 0.05, 0.95);

  // Derivative-based AA on both sides of the ridge. fwidth(ramp) ≈ 1 / (ridge
  // height in pixels). The fall side (ramp near 1) has a sub-pixel-wide steep
  // pow drop; the rise side (ramp near 0) has infinite slope at the valley
  // boundary. Both alias as stair-steps along the ridge edge when the warp
  // field shifts those boundaries by fractional pixels between columns. We
  // smoothstep the first and last pixel-width of each ridge; the pow curves
  // remain across the interior where slopes are pixel-scale or gentler.
  float rw = fwidth(ramp);
  float aaWindow = rw * 2.0;
  float value;
  if (ramp < aaWindow) {
    // Rise-side AA: blend from 0 up to the pow curve's value at aaWindow so
    // the transition into the pow segment stays continuous.
    float aaRiseTop = pow(aaWindow / peak, u_riseCurve);
    value = smoothstep(0.0, aaWindow, ramp) * aaRiseTop;
  } else if (ramp < peak) {
    value = pow(ramp / peak, u_riseCurve);
  } else if ((1.0 - peak) < aaWindow) {
    float aaFallEnd = min(peak + aaWindow, 1.0);
    value = 1.0 - smoothstep(peak, aaFallEnd, ramp);
  } else {
    value = pow(1.0 - (ramp - peak) / (1.0 - peak), u_fallCurve);
  }

  value = mix(u_shadow, u_highlight, value);
  value *= mix(1.0, n1, u_noiseLit);
  value = mix(value, 1.0 - value, u_invert);

  vec3 composite = overlayBlend(vec3(value), surface);
  gl_FragColor = vec4(applyWebappOverlay(composite, uv), 1.0);
}`
}

export const mobileDefaults = {
  ridgeCountTarget: 35,
  ridgeGap: 0.0,
  ridgeFlip: 0,
  ridgeDual: 0,
  ridgesEnabled: 1,
  angle: 130.0,
  shadow: 0.45,
  highlight: 0.55,
  peakPos: 0.95,
  riseCurve: 0.6,
  fallCurve: 0.1,
  invert: 0,
  warpScale: 0.8,
  warpAmount: 0.1,
  noiseOffsetX: 0.6,
  noiseOffsetY: -1.2,
  noiseOctaves: 2,
  noiseInvert: 0,
  noiseLit: 0.0,
  ...colorDefaults,
  ...animDefaults,
}

export const tierDefaults = {
  desktop: { ridgeCountTarget: 70, noiseOctaves: 2, warpScale: 1.2 },
}

export function createUniforms(gl, program) {
  return {
    u_resolution: gl.getUniformLocation(program, 'u_resolution'),
    u_noiseTex: gl.getUniformLocation(program, 'u_noiseTex'),
    u_noiseTexSize: gl.getUniformLocation(program, 'u_noiseTexSize'),
    u_frequency: gl.getUniformLocation(program, 'u_frequency'),
    u_ridgeCount: gl.getUniformLocation(program, 'u_ridgeCount'),
    u_ridgeOffset: gl.getUniformLocation(program, 'u_ridgeOffset'),
    u_ridgeOffsetB: gl.getUniformLocation(program, 'u_ridgeOffsetB'),
    u_ridgeDual: gl.getUniformLocation(program, 'u_ridgeDual'),
    u_ridgesEnabled: gl.getUniformLocation(program, 'u_ridgesEnabled'),
    u_angleRot: gl.getUniformLocation(program, 'u_angleRot'),
    u_shadow: gl.getUniformLocation(program, 'u_shadow'),
    u_highlight: gl.getUniformLocation(program, 'u_highlight'),
    u_peakPos: gl.getUniformLocation(program, 'u_peakPos'),
    u_riseCurve: gl.getUniformLocation(program, 'u_riseCurve'),
    u_fallCurve: gl.getUniformLocation(program, 'u_fallCurve'),
    u_invert: gl.getUniformLocation(program, 'u_invert'),
    u_warpAmount: gl.getUniformLocation(program, 'u_warpAmount'),
    u_warpMargin: gl.getUniformLocation(program, 'u_warpMargin'),
    u_noiseLit: gl.getUniformLocation(program, 'u_noiseLit'),
    u_animPeakEnabled: gl.getUniformLocation(program, 'u_animPeakEnabled'),
    u_animPeakCentre: gl.getUniformLocation(program, 'u_animPeakCentre'),
    u_animPeakAmplitude: gl.getUniformLocation(program, 'u_animPeakAmplitude'),
    u_animPeakSpeed: gl.getUniformLocation(program, 'u_animPeakSpeed'),
    u_animPeakStagger: gl.getUniformLocation(program, 'u_animPeakStagger'),
    u_animTime: gl.getUniformLocation(program, 'u_animTime'),
    ...createColorUniforms(gl, program),
  }
}

export function setUniforms(gl, uniforms, canvas, state) {
  gl.uniform2f(uniforms.u_resolution, canvas.width, canvas.height)
  const rad = (state.angle * Math.PI) / 180
  const span = Math.abs(Math.sin(rad)) + Math.abs(Math.cos(rad))

  const freq = state.ridgeCountTarget / span
  const totalRidges = state.ridgeCountTarget
  const gapRidges = state.ridgeGap * totalRidges
  const effectiveDual = state.ridgeDual && state.ridgeGap > 0
  const rawCount = effectiveDual
    ? (totalRidges - gapRidges) / 2
    : totalRidges - gapRidges
  const ridgeCount = state.ridgeGap > 0 ? Math.floor(rawCount) : rawCount

  const offsetMin = (freq * (span - 1)) / 2
  const offsetMax = ridgeCount - (freq * (1 + span)) / 2
  const ridgeOffset = state.ridgeFlip ? offsetMax : offsetMin
  const ridgeOffsetB = state.ridgeFlip ? offsetMin : offsetMax

  gl.uniform1f(uniforms.u_frequency, freq)
  gl.uniform1f(uniforms.u_ridgeCount, Math.max(0, ridgeCount))
  gl.uniform1f(uniforms.u_ridgeOffset, ridgeOffset)
  gl.uniform1f(uniforms.u_ridgeOffsetB, ridgeOffsetB)
  gl.uniform1f(uniforms.u_ridgeDual, effectiveDual ? 1.0 : 0.0)
  gl.uniform1f(uniforms.u_ridgesEnabled, state.ridgesEnabled ? 1.0 : 0.0)
  gl.uniform2f(uniforms.u_angleRot, Math.cos(rad), Math.sin(rad))
  gl.uniform1f(uniforms.u_shadow, state.shadow)
  gl.uniform1f(uniforms.u_highlight, state.highlight)
  gl.uniform1f(uniforms.u_peakPos, state.peakPos)
  gl.uniform1f(uniforms.u_riseCurve, state.riseCurve)
  gl.uniform1f(uniforms.u_fallCurve, state.fallCurve)
  gl.uniform1f(uniforms.u_invert, state.invert ? 1.0 : 0.0)
  gl.uniform1f(uniforms.u_warpAmount, state.warpAmount)
  gl.uniform1f(uniforms.u_warpMargin, Math.ceil(freq * state.warpAmount) + 1.0)
  gl.uniform1f(uniforms.u_noiseLit, state.noiseLit)
  gl.uniform1f(uniforms.u_animPeakEnabled, state.animPeakEnabled ? 1.0 : 0.0)
  gl.uniform1f(uniforms.u_animPeakCentre, state.animPeakCentre)
  gl.uniform1f(uniforms.u_animPeakAmplitude, state.animPeakAmplitude)
  gl.uniform1f(uniforms.u_animPeakSpeed, state.animPeakSpeed)
  gl.uniform1f(uniforms.u_animPeakStagger, state.animPeakStagger)
  gl.uniform1f(uniforms.u_animTime, state.animTime)
  setColorUniforms(gl, uniforms, state)
}

export function createPrepassUniforms(gl, program) {
  return {
    u_resolution: gl.getUniformLocation(program, 'u_resolution'),
    u_angleRot: gl.getUniformLocation(program, 'u_angleRot'),
    u_warpScale: gl.getUniformLocation(program, 'u_warpScale'),
    u_noiseOffset: gl.getUniformLocation(program, 'u_noiseOffset'),
    u_noiseInvert: gl.getUniformLocation(program, 'u_noiseInvert'),
    u_animMoveEnabled: gl.getUniformLocation(program, 'u_animMoveEnabled'),
    u_animMoveSpeed: gl.getUniformLocation(program, 'u_animMoveSpeed'),
    u_animTime: gl.getUniformLocation(program, 'u_animTime'),
  }
}

export function setPrepassUniforms(gl, uniforms, fboWidth, fboHeight, state) {
  gl.uniform2f(uniforms.u_resolution, fboWidth, fboHeight)
  const rad = (state.angle * Math.PI) / 180
  gl.uniform2f(uniforms.u_angleRot, Math.cos(rad), Math.sin(rad))
  gl.uniform1f(uniforms.u_warpScale, state.warpScale)
  gl.uniform2f(uniforms.u_noiseOffset, state.noiseOffsetX, state.noiseOffsetY)
  gl.uniform1f(uniforms.u_noiseInvert, state.noiseInvert ? 1.0 : 0.0)
  gl.uniform1f(uniforms.u_animMoveEnabled, state.animMoveEnabled ? 1.0 : 0.0)
  gl.uniform1f(uniforms.u_animMoveSpeed, state.animMoveSpeed)
  gl.uniform1f(uniforms.u_animTime, state.animTime)
}

export function makePostFragSrc() {
  return `
#extension GL_OES_standard_derivatives : enable
precision ${FRAG_PRECISION} float;

uniform sampler2D u_colorTex;
uniform sampler2D u_noiseTex;
uniform vec2 u_noiseTexSize;
uniform vec2 u_resolution;
uniform vec2 u_angleRot;
uniform float u_warpAmount;
uniform float u_blurRadius;

vec4 smoothTex(sampler2D tex, vec2 uv, vec2 texSize) {
  vec2 t = uv * texSize + 0.5;
  vec2 i = floor(t);
  vec2 f = t - i;
  f = f * f * (3.0 - 2.0 * f);
  vec2 smoothed = (i + f - 0.5) / texSize;
  return texture2D(tex, smoothed);
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution;

  // Recover screen-space gradient of st.y. The main pass computes
  //   st.y = -sa*(uv.x-0.5) + ca*(uv.y-0.5) + 0.5 + 2*warpAmount*(n2 - 0.5)
  // so d(st.y)/d(pixel) = (-sa/W + 2*wa*dFdx(n2),  ca/H + 2*wa*dFdy(n2)).
  // dFdx/dFdy of a texture sample gives the per-screen-pixel derivative of n2
  // directly (the canvas_width factor cancels out inside the derivative).
  float n2 = smoothTex(u_noiseTex, uv, u_noiseTexSize).g;
  float dn2x = dFdx(n2);
  float dn2y = dFdy(n2);

  float ca = u_angleRot.x;
  float sa = u_angleRot.y;
  vec2 normalPx = vec2(
    -sa / u_resolution.x + 2.0 * u_warpAmount * dn2x,
     ca / u_resolution.y + 2.0 * u_warpAmount * dn2y
  );

  // Tangent = 90° rotation of normal. Fallback to base angle if gradient is
  // numerically near zero (would only happen with warpAmount 0 on a perfectly
  // axis-aligned ridge — unlikely in production but worth guarding).
  vec2 tangentPx = length(normalPx) > 1e-6
    ? normalize(vec2(normalPx.y, -normalPx.x))
    : vec2(ca, sa);

  // Per-pixel UV offset along tangent — divide by (W, H) to cancel the
  // aspect-ratio distortion between pixel space and UV space.
  vec2 stepUV = tangentPx / u_resolution;

  // 9-tap uniform box blur centred on the fragment. Wider kernel than the
  // initial 5-tap so it covers step patterns up to ~4 pixels on a side at
  // radius 1. At radius > 1.5 taps start spacing out enough to undersample,
  // so prefer wider kernel + reasonable radius over narrow kernel + high radius.
  vec3 accum = vec3(0.0);
  for (int i = -4; i <= 4; i++) {
    accum += texture2D(u_colorTex, uv + stepUV * float(i) * u_blurRadius).rgb;
  }
  gl_FragColor = vec4(accum / 9.0, 1.0);
}`
}

export function createPostUniforms(gl, program) {
  return {
    u_colorTex: gl.getUniformLocation(program, 'u_colorTex'),
    u_noiseTex: gl.getUniformLocation(program, 'u_noiseTex'),
    u_noiseTexSize: gl.getUniformLocation(program, 'u_noiseTexSize'),
    u_resolution: gl.getUniformLocation(program, 'u_resolution'),
    u_angleRot: gl.getUniformLocation(program, 'u_angleRot'),
    u_warpAmount: gl.getUniformLocation(program, 'u_warpAmount'),
    u_blurRadius: gl.getUniformLocation(program, 'u_blurRadius'),
  }
}

export function setPostUniforms(gl, uniforms, canvas, state, blurRadius) {
  gl.uniform2f(uniforms.u_resolution, canvas.width, canvas.height)
  const rad = (state.angle * Math.PI) / 180
  gl.uniform2f(uniforms.u_angleRot, Math.cos(rad), Math.sin(rad))
  gl.uniform1f(uniforms.u_warpAmount, state.warpAmount)
  gl.uniform1f(uniforms.u_blurRadius, blurRadius)
}
